import React, { useState, useEffect } from 'react';
import { ThemeConfig, ThemeColors } from '@/types/theme';
import { SimpleThemeColors, generateThemeFromSimple, generateThemeWithVariation, extractSimpleColors, validateSimpleColors, generateThemePreview } from '@/utils/themeGenerator';
import { validateThemeContrast, autoFixThemeContrast } from '@/utils/themeUtils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Separator } from '@/components/ui/separator';
import { 
  Palette, 
  Settings, 
  Eye, 
  CheckCircle, 
  AlertCircle, 
  Sparkles,
  Wand2,
  Layers,
  RefreshCw,
  Zap,
  Paintbrush,
  Monitor,
  Smartphone,
  X
} from 'lucide-react';

interface EnhancedThemeEditorProps {
  theme?: ThemeConfig;
  onSave: (theme: Omit<ThemeConfig, 'id' | 'createdAt'>) => void;
  onCancel: () => void;
  isEditing?: boolean;
}

export const EnhancedThemeEditor: React.FC<EnhancedThemeEditorProps> = ({ 
  theme, 
  onSave, 
  onCancel, 
  isEditing = false 
}) => {
  const [mode, setMode] = useState<'simple' | 'advanced'>('simple');
  const [showPreview, setShowPreview] = useState(true);
  const [isRegenerating, setIsRegenerating] = useState(false);
  
  // Simple mode state
  const [simpleColors, setSimpleColors] = useState<SimpleThemeColors>({
    primary: '#BEA04D',
    secondary: '#CC9C00',
    accent: '#F3ECD9',
    background: '#ffffff',
    foreground: '#000000',
    destructive: '#ef4444',
  });

  // Advanced mode state
  const [advancedColors, setAdvancedColors] = useState<ThemeColors>({
    primary: '#BEA04D',
    primaryHover: '#A38A41',
    primaryLight: '#F3ECD9',
    primaryForeground: '#000000',
    secondary: '#CC9C00',
    secondaryHover: '#B38A00',
    secondaryForeground: '#000000',
    accent: '#F3ECD9',
    accentHover: '#E8DCC0',
    accentForeground: '#000000',
    background: '#ffffff',
    foreground: '#000000',
    muted: '#f5f5f5',
    mutedForeground: '#6b7280',
    border: '#e5e7eb',
    input: '#ffffff',
    ring: '#BEA04D',
    destructive: '#ef4444',
    destructiveForeground: '#ffffff',
    card: '#ffffff',
    cardForeground: '#000000',
    popover: '#ffffff',
    popoverForeground: '#000000',
    sidebarColor: '#000000',
    sidebarTextColor: '#ffffff',
    sidebarHoverBgColor: '#374151',
    sidebarHoverTextColor: '#ffffff',
  });

  // Form state
  const [formData, setFormData] = useState<Omit<ThemeConfig, 'id' | 'createdAt'>>({
    name: theme?.name || '',
    description: theme?.description || '',
    isCustom: true,
    colors: theme?.colors || advancedColors,
  });

  // Initialize form data from existing theme
  useEffect(() => {
    if (theme) {
      setFormData({
        name: theme.name,
        description: theme.description,
        isCustom: true,
        colors: theme.colors,
      });
      
      // Extract simple colors for simple mode
      const extracted = extractSimpleColors(theme.colors);
      setSimpleColors(extracted);
      setAdvancedColors(theme.colors);
    }
  }, [theme]);

  // Update form data when colors change
  useEffect(() => {
    if (mode === 'simple') {
      const generatedColors = generateThemeFromSimple(simpleColors);
      setFormData(prev => ({
        ...prev,
        colors: generatedColors,
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        colors: advancedColors,
      }));
    }
  }, [simpleColors, advancedColors, mode]);

  const handleSimpleColorChange = (key: keyof SimpleThemeColors, value: string) => {
    setSimpleColors(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleAdvancedColorChange = (key: keyof ThemeColors, value: string) => {
    setAdvancedColors(prev => ({
      ...prev,
      [key]: value,
    }));
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    
    // Auto-fix any contrast issues before saving
    const fixedTheme = autoFixThemeContrast({
      ...formData,
      id: 'temp',
      createdAt: new Date().toISOString()
    });
    onSave(fixedTheme);
  };

  const handleRegenerateColors = () => {
    setIsRegenerating(true);
    
    // Add a small delay to show the loading state
    setTimeout(() => {
      // Regenerate colors with variation from the current simple colors
      const regeneratedColors = generateThemeWithVariation(simpleColors);
      setFormData(prev => ({
        ...prev,
        colors: regeneratedColors,
      }));
      setIsRegenerating(false);
    }, 200);
  };

  const simpleValidation = validateSimpleColors(simpleColors);
  const contrastValidation = validateThemeContrast({
    ...formData,
    id: 'temp',
    createdAt: new Date().toISOString()
  });
  const themePreview = generateThemePreview(simpleColors);

  const simpleColorFields: Array<{ key: keyof SimpleThemeColors; label: string; description: string; icon: React.ReactNode }> = [
    { key: 'primary', label: 'Primary', description: 'Main brand color for buttons and links', icon: <Palette className="w-4 h-4" /> },
    { key: 'secondary', label: 'Secondary', description: 'Secondary brand color for highlights', icon: <Paintbrush className="w-4 h-4" /> },
    { key: 'accent', label: 'Accent', description: 'Accent color for special elements', icon: <Zap className="w-4 h-4" /> },
    { key: 'background', label: 'Background', description: 'Main page background color', icon: <Monitor className="w-4 h-4" /> },
    { key: 'foreground', label: 'Foreground', description: 'Main text color', icon: <Smartphone className="w-4 h-4" /> },
    { key: 'destructive', label: 'Destructive', description: 'Error and danger color', icon: <X className="w-4 h-4" /> },
  ];

  const advancedColorFields: Array<{ key: keyof ThemeColors; label: string; description: string; category: string }> = [
    // Primary colors
    { key: 'primary', label: 'Primary', description: 'Main brand color', category: 'Primary' },
    { key: 'primaryHover', label: 'Primary Hover', description: 'Primary color on hover', category: 'Primary' },
    { key: 'primaryLight', label: 'Primary Light', description: 'Light variant of primary', category: 'Primary' },
    { key: 'primaryForeground', label: 'Primary Text', description: 'Text color on primary backgrounds', category: 'Primary' },
    
    // Secondary colors
    { key: 'secondary', label: 'Secondary', description: 'Secondary brand color', category: 'Secondary' },
    { key: 'secondaryHover', label: 'Secondary Hover', description: 'Secondary color on hover', category: 'Secondary' },
    { key: 'secondaryForeground', label: 'Secondary Text', description: 'Text color on secondary backgrounds', category: 'Secondary' },
    
    // Accent colors
    { key: 'accent', label: 'Accent', description: 'Accent color for highlights', category: 'Accent' },
    { key: 'accentHover', label: 'Accent Hover', description: 'Accent color on hover', category: 'Accent' },
    { key: 'accentForeground', label: 'Accent Text', description: 'Text color on accent backgrounds', category: 'Accent' },
    
    // Base colors
    { key: 'background', label: 'Background', description: 'Main background color', category: 'Base' },
    { key: 'foreground', label: 'Foreground', description: 'Main text color', category: 'Base' },
    
    // UI colors
    { key: 'muted', label: 'Muted', description: 'Muted background color', category: 'UI' },
    { key: 'mutedForeground', label: 'Muted Text', description: 'Muted text color', category: 'UI' },
    { key: 'border', label: 'Border', description: 'Border color', category: 'UI' },
    { key: 'input', label: 'Input', description: 'Input background color', category: 'UI' },
    { key: 'ring', label: 'Ring', description: 'Focus ring color', category: 'UI' },
    
    // Destructive colors
    { key: 'destructive', label: 'Destructive', description: 'Error/danger color', category: 'Destructive' },
    { key: 'destructiveForeground', label: 'Destructive Text', description: 'Text on destructive background', category: 'Destructive' },
    
    // Card colors
    { key: 'card', label: 'Card', description: 'Card background color', category: 'Card' },
    { key: 'cardForeground', label: 'Card Text', description: 'Card text color', category: 'Card' },
    
    // Popover colors
    { key: 'popover', label: 'Popover', description: 'Popover background color', category: 'Popover' },
    { key: 'popoverForeground', label: 'Popover Text', description: 'Popover text color', category: 'Popover' },
    
    // Sidebar colors
    { key: 'sidebarColor', label: 'Sidebar', description: 'Sidebar background color', category: 'Sidebar' },
    { key: 'sidebarTextColor', label: 'Sidebar Text', description: 'Sidebar text color', category: 'Sidebar' },
    { key: 'sidebarHoverBgColor', label: 'Sidebar Hover', description: 'Sidebar hover background', category: 'Sidebar' },
    { key: 'sidebarHoverTextColor', label: 'Sidebar Hover Text', description: 'Sidebar hover text color', category: 'Sidebar' },
  ];

  const groupedAdvancedFields = advancedColorFields.reduce((acc, field) => {
    if (!acc[field.category]) {
      acc[field.category] = [];
    }
    acc[field.category].push(field);
    return acc;
  }, {} as Record<string, typeof advancedColorFields>);

  return (
    <div className="min-h-[600px] bg-gradient-to-br from-muted/30 to-muted/60 p-6 rounded-xl">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center space-x-3">
            <div className="p-2 bg-primary/10 rounded-lg">
              <Palette className="w-6 h-6 text-primary" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-foreground">
                {isEditing ? 'Edit Theme' : 'Create Theme'}
              </h2>
              <p className="text-muted-foreground">
                {isEditing ? 'Customize your existing theme' : 'Design a beautiful new theme'}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <Button
              type="button"
              variant={showPreview ? "default" : "outline"}
              size="sm"
              onClick={() => setShowPreview(!showPreview)}
              className="flex items-center space-x-2"
            >
              <Eye className="w-4 h-4" />
              <span>Preview</span>
            </Button>
          </div>
        </div>

        {/* Mode Toggle */}
        <Tabs value={mode} onValueChange={(value) => setMode(value as 'simple' | 'advanced')} className="w-full">
          <TabsList className="grid w-full grid-cols-2 bg-background/80 backdrop-blur-sm border border-border">
            <TabsTrigger value="simple" className="flex items-center space-x-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Sparkles className="w-4 h-4" />
              <span>Simple Mode</span>
            </TabsTrigger>
            <TabsTrigger value="advanced" className="flex items-center space-x-2 data-[state=active]:bg-primary data-[state=active]:text-primary-foreground">
              <Settings className="w-4 h-4" />
              <span>Advanced Mode</span>
            </TabsTrigger>
          </TabsList>

          {/* Simple Mode */}
          <TabsContent value="simple" className="mt-6">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
              {/* Left Column - Color Picker */}
              <div className="space-y-6">
                <Card className="border-0 shadow-lg bg-background/80 backdrop-blur-sm">
                  <CardHeader className="pb-4">
                    <div className="flex items-center space-x-2">
                      <div className="p-1.5 bg-primary/10 rounded-md">
                        <Wand2 className="w-4 h-4 text-primary" />
                      </div>
                      <div>
                        <CardTitle className="text-lg">Main Colors</CardTitle>
                        <CardDescription>
                          Choose your 6 main colors and we'll generate the rest automatically
                        </CardDescription>
                      </div>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-6">
                    {/* Basic Info */}
                    <div className="space-y-4">
                      <div>
                        <Label htmlFor="theme-name" className="text-sm font-medium text-foreground">Theme Name</Label>
                        <Input
                          id="theme-name"
                          value={formData.name}
                          onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                          placeholder="Enter a memorable name"
                          className="mt-1 border-border focus:border-primary focus:ring-primary"
                          required
                        />
                      </div>
                      
                      <div>
                        <Label htmlFor="theme-description" className="text-sm font-medium text-foreground">Description</Label>
                        <Input
                          id="theme-description"
                          value={formData.description}
                          onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                          placeholder="Describe your theme"
                          className="mt-1 border-border focus:border-primary focus:ring-primary"
                          required
                        />
                      </div>
                    </div>

                    {/* Simple Color Fields */}
                    <div className="space-y-4">
                      {simpleColorFields.map(({ key, label, description, icon }) => (
                        <div key={key} className="group">
                          <div className="flex items-center space-x-2 mb-2">
                            <div className="p-1 bg-muted rounded text-muted-foreground group-hover:bg-primary/10 group-hover:text-primary transition-colors">
                              {icon}
                            </div>
                            <Label htmlFor={`simple-color-${key}`} className="text-sm font-medium text-foreground">
                              {label}
                            </Label>
                          </div>
                          <div className="flex items-center space-x-3">
                            <div className="relative">
                              <Input
                                id={`simple-color-${key}`}
                                type="color"
                                value={simpleColors[key]}
                                onChange={(e) => handleSimpleColorChange(key, e.target.value)}
                                className="w-12 h-12 p-1 border-2 border-border rounded-lg cursor-pointer hover:border-primary transition-colors"
                              />
                              <div className="absolute inset-0 rounded-lg shadow-inner pointer-events-none" />
                            </div>
                            <Input
                              type="text"
                              value={simpleColors[key]}
                              onChange={(e) => handleSimpleColorChange(key, e.target.value)}
                              placeholder="#000000"
                              className="flex-1 font-mono text-sm border-border focus:border-primary focus:ring-primary"
                            />
                          </div>
                          <p className="text-xs text-muted-foreground mt-1">{description}</p>
                        </div>
                      ))}
                    </div>

                    {/* Validation Messages */}
                    {!simpleValidation.isValid && (
                      <Alert variant="destructive" className="">
                        <AlertCircle className="h-4 w-4" />
                        <AlertDescription>
                          <ul className="list-disc list-inside space-y-1 text-sm">
                            {simpleValidation.issues.map((issue, index) => (
                              <li key={index}>{issue}</li>
                            ))}
                          </ul>
                        </AlertDescription>
                      </Alert>
                    )}

                    {!contrastValidation.isValid && (
                      <Alert className="border-amber-200 bg-amber-50">
                        <AlertCircle className="h-4 w-4 text-amber-600" />
                        <AlertDescription className="text-amber-800">
                          <div className="flex items-center justify-between">
                            <span>Some contrast issues detected. They will be automatically fixed when saving.</span>
                            <Badge variant="secondary" className="ml-2 bg-amber-100 text-amber-800">
                              {contrastValidation.issues.length} issues
                            </Badge>
                          </div>
                        </AlertDescription>
                      </Alert>
                    )}
                  </CardContent>
                </Card>
              </div>

              {/* Right Column - Generated Colors & Preview */}
              <div className="space-y-6">
                {/* Generated Colors */}
                <Card className="border-0 shadow-lg bg-background/80 backdrop-blur-sm">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-2">
                        <div className="p-1.5 bg-secondary/10 rounded-md">
                          <Layers className="w-4 h-4 text-secondary" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">Generated Colors</CardTitle>
                          <CardDescription>
                            Automatically generated from your main colors
                          </CardDescription>
                        </div>
                      </div>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        onClick={handleRegenerateColors}
                        disabled={isRegenerating}
                        className="flex items-center space-x-2 border-border hover:border-primary hover:bg-primary/5"
                      >
                        <RefreshCw className={`w-4 h-4 ${isRegenerating ? 'animate-spin' : ''}`} />
                        <span>{isRegenerating ? 'Generating...' : 'Regenerate'}</span>
                      </Button>
                    </div>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-3">
                      {/* Primary Colors */}
                      <div className="space-y-2">
                        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Primary</div>
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <div 
                              className="w-6 h-6 rounded border-2 border-border shadow-sm"
                              style={{ backgroundColor: formData.colors.primaryHover }}
                            />
                            <div className="text-xs font-mono text-muted-foreground">Hover</div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div 
                              className="w-6 h-6 rounded border-2 border-border shadow-sm"
                              style={{ backgroundColor: formData.colors.primaryLight }}
                            />
                            <div className="text-xs font-mono text-muted-foreground">Light</div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div 
                              className="w-6 h-6 rounded border-2 border-border shadow-sm"
                              style={{ backgroundColor: formData.colors.primaryForeground }}
                            />
                            <div className="text-xs font-mono text-muted-foreground">Text</div>
                          </div>
                        </div>
                      </div>

                      {/* Secondary Colors */}
                      <div className="space-y-2">
                        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Secondary</div>
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <div 
                              className="w-6 h-6 rounded border-2 border-border shadow-sm"
                              style={{ backgroundColor: formData.colors.secondaryHover }}
                            />
                            <div className="text-xs font-mono text-muted-foreground">Hover</div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div 
                              className="w-6 h-6 rounded border-2 border-border shadow-sm"
                              style={{ backgroundColor: formData.colors.secondaryForeground }}
                            />
                            <div className="text-xs font-mono text-muted-foreground">Text</div>
                          </div>
                        </div>
                      </div>

                      {/* Accent Colors */}
                      <div className="space-y-2">
                        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Accent</div>
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <div 
                              className="w-6 h-6 rounded border-2 border-border shadow-sm"
                              style={{ backgroundColor: formData.colors.accentHover }}
                            />
                            <div className="text-xs font-mono text-muted-foreground">Hover</div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div 
                              className="w-6 h-6 rounded border-2 border-border shadow-sm"
                              style={{ backgroundColor: formData.colors.accentForeground }}
                            />
                            <div className="text-xs font-mono text-muted-foreground">Text</div>
                          </div>
                        </div>
                      </div>

                      {/* UI Colors */}
                      <div className="space-y-2">
                        <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">UI</div>
                        <div className="space-y-2">
                          <div className="flex items-center space-x-2">
                            <div 
                              className="w-6 h-6 rounded border-2 border-border shadow-sm"
                              style={{ backgroundColor: formData.colors.muted }}
                            />
                            <div className="text-xs font-mono text-muted-foreground">Muted</div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div 
                              className="w-6 h-6 rounded border-2 border-border shadow-sm"
                              style={{ backgroundColor: formData.colors.border }}
                            />
                            <div className="text-xs font-mono text-muted-foreground">Border</div>
                          </div>
                          <div className="flex items-center space-x-2">
                            <div 
                              className="w-6 h-6 rounded border-2 border-border shadow-sm"
                              style={{ backgroundColor: formData.colors.card }}
                            />
                            <div className="text-xs font-mono text-muted-foreground">Card</div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Live Preview */}
                {showPreview && (
                  <Card className="border-0 shadow-lg bg-background/80 backdrop-blur-sm">
                    <CardHeader className="pb-4">
                      <div className="flex items-center space-x-2">
                        <div className="p-1.5 bg-accent/10 rounded-md">
                          <Monitor className="w-4 h-4 text-accent-foreground" />
                        </div>
                        <div>
                          <CardTitle className="text-lg">Live Preview</CardTitle>
                          <CardDescription>
                            See how your theme will look in the app
                          </CardDescription>
                        </div>
                      </div>
                    </CardHeader>
                    <CardContent>
                      <div className="space-y-4">
                        {/* Buttons */}
                        <div className="space-y-2">
                          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Buttons</div>
                          <div className="flex flex-wrap gap-2">
                            <button 
                              className="px-4 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105"
                              style={{ 
                                backgroundColor: themePreview.preview.primary.background,
                                color: themePreview.preview.primary.text
                              }}
                            >
                              Primary
                            </button>
                            <button 
                              className="px-4 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105"
                              style={{ 
                                backgroundColor: themePreview.preview.secondary.background,
                                color: themePreview.preview.secondary.text
                              }}
                            >
                              Secondary
                            </button>
                            <button 
                              className="px-4 py-2 rounded-lg text-sm font-medium transition-all hover:scale-105"
                              style={{ 
                                backgroundColor: themePreview.preview.destructive.background,
                                color: themePreview.preview.destructive.text
                              }}
                            >
                              Destructive
                            </button>
                          </div>
                        </div>

                        {/* Card */}
                        <div className="space-y-2">
                          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Card</div>
                          <div 
                            className="p-4 rounded-lg border transition-all hover:shadow-md"
                            style={{ 
                              backgroundColor: themePreview.preview.card.background,
                              color: themePreview.preview.card.text,
                              borderColor: themePreview.preview.card.border
                            }}
                          >
                            <h5 className="font-medium mb-2">Sample Card</h5>
                            <p className="text-sm opacity-80">This is how cards will look with your theme.</p>
                          </div>
                        </div>

                        {/* Sidebar */}
                        <div className="space-y-2">
                          <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide">Sidebar</div>
                          <div 
                            className="p-3 rounded-lg transition-all"
                            style={{ 
                              backgroundColor: themePreview.preview.sidebar.background,
                              color: themePreview.preview.sidebar.text
                            }}
                          >
                            <div className="space-y-2">
                              <div className="text-sm font-medium">Menu Item</div>
                              <div 
                                className="text-sm p-2 rounded transition-all"
                                style={{ 
                                  backgroundColor: themePreview.preview.sidebar.hover,
                                  color: themePreview.preview.sidebar.hoverText
                                }}
                              >
                                Hovered Item
                              </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Advanced Mode */}
          <TabsContent value="advanced" className="mt-6">
            <Card className="border-0 shadow-lg bg-background/80 backdrop-blur-sm">
              <CardHeader>
                <div className="flex items-center space-x-2">
                  <div className="p-1.5 bg-primary/10 rounded-md">
                    <Layers className="w-4 h-4 text-primary" />
                  </div>
                  <div>
                    <CardTitle className="text-lg">Advanced Mode</CardTitle>
                    <CardDescription>
                      Full control over all theme colors. Perfect for designers who want complete customization.
                    </CardDescription>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-8">
                  {/* Basic Info */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="advanced-theme-name" className="text-sm font-medium text-foreground">Theme Name</Label>
                      <Input
                        id="advanced-theme-name"
                        value={formData.name}
                        onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="Enter theme name"
                        className="mt-1 border-border focus:border-primary focus:ring-primary"
                        required
                      />
                    </div>
                    
                    <div>
                      <Label htmlFor="advanced-theme-description" className="text-sm font-medium text-foreground">Description</Label>
                      <Input
                        id="advanced-theme-description"
                        value={formData.description}
                        onChange={(e) => setFormData(prev => ({ ...prev, description: e.target.value }))}
                        placeholder="Enter theme description"
                        className="mt-1 border-border focus:border-primary focus:ring-primary"
                        required
                      />
                    </div>
                  </div>

                  {/* Advanced Color Fields */}
                  <div className="space-y-8">
                    {Object.entries(groupedAdvancedFields).map(([category, fields]) => (
                      <div key={category}>
                        <div className="flex items-center space-x-2 mb-4">
                          <h3 className="text-lg font-semibold text-foreground">{category} Colors</h3>
                          <div className="h-px flex-1 bg-gradient-to-r from-slate-200 to-transparent" />
                        </div>
                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                          {fields.map(({ key, label, description }) => (
                            <div key={key} className="group">
                              <Label htmlFor={`advanced-color-${key}`} className="text-sm font-medium text-foreground">
                                {label}
                              </Label>
                              <div className="flex items-center space-x-3 mt-1">
                                <div className="relative">
                                  <Input
                                    id={`advanced-color-${key}`}
                                    type="color"
                                    value={advancedColors[key]}
                                    onChange={(e) => handleAdvancedColorChange(key, e.target.value)}
                                    className="w-12 h-12 p-1 border-2 border-border rounded-lg cursor-pointer hover:border-primary transition-colors"
                                  />
                                  <div className="absolute inset-0 rounded-lg shadow-inner pointer-events-none" />
                                </div>
                                <Input
                                  type="text"
                                  value={advancedColors[key]}
                                  onChange={(e) => handleAdvancedColorChange(key, e.target.value)}
                                  placeholder="#000000"
                                  className="flex-1 font-mono text-sm border-border focus:border-primary focus:ring-primary"
                                />
                              </div>
                              <p className="text-xs text-muted-foreground mt-1">{description}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Validation Messages */}
                  {!contrastValidation.isValid && (
                    <Alert variant="destructive" className="">
                      <AlertCircle className="h-4 w-4" />
                      <AlertDescription>
                        <div className="space-y-2">
                          <div className="flex items-center justify-between">
                            <span>Contrast issues detected:</span>
                            <Badge variant="destructive" className="ml-2">
                              {contrastValidation.issues.length} issues
                            </Badge>
                          </div>
                          <ul className="list-disc list-inside space-y-1 text-sm">
                            {contrastValidation.issues.map((issue, index) => (
                              <li key={index}>
                                <strong>{issue.element}:</strong> {issue.suggestion}
                              </li>
                            ))}
                          </ul>
                        </div>
                      </AlertDescription>
                    </Alert>
                  )}
                </form>
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>
      </div>

      {/* Action Buttons */}
      <div className="flex justify-end space-x-3 pt-6 border-t border-border">
        <Button 
          type="button" 
          variant="outline" 
          onClick={onCancel}
          className="border-border hover:border-primary hover:bg-primary hover:text-primary-foreground"
        >
          Cancel
        </Button>
        <Button 
          type="submit" 
          onClick={handleSubmit}
          disabled={!simpleValidation.isValid}
          className="bg-primary hover:bg-primaryHover text-primary-foreground shadow-lg hover:shadow-xl transition-all"
        >
          <CheckCircle className="w-4 h-4 mr-2" />
          {isEditing ? 'Update Theme' : 'Create Theme'}
        </Button>
      </div>
    </div>
  );
}; 